import { useCallback, useEffect, useMemo, useState } from 'react';

type CarouselDirection = 'next' | 'prev';

interface UseResponsiveCarouselOptions {
  desktopVisibleCount?: number;
  mobileVisibleCount?: number;
  mobileBreakpoint?: number;
  transitionMs?: number;
  autoPlayMs?: number;
}

interface VisibleCarouselItem<T> {
  item: T;
  originalIndex: number;
  position: number;
}

const wrapIndex = (index: number, length: number) => {
  if (length === 0) return 0;
  return ((index % length) + length) % length;
};

const getInitialVisibleCount = (
  desktopVisibleCount: number,
  mobileVisibleCount: number,
  mobileBreakpoint: number
) => {
  if (typeof window === 'undefined') return desktopVisibleCount;
  return window.innerWidth <= mobileBreakpoint ? mobileVisibleCount : desktopVisibleCount;
};

export const useResponsiveCarousel = <T,>(
  items: T[],
  {
    desktopVisibleCount = 3,
    mobileVisibleCount = 1,
    mobileBreakpoint = 1024,
    transitionMs = 420,
    autoPlayMs,
  }: UseResponsiveCarouselOptions = {}
) => {
  const [visibleCount, setVisibleCount] = useState(() =>
    getInitialVisibleCount(desktopVisibleCount, mobileVisibleCount, mobileBreakpoint)
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<CarouselDirection>('next');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const updateVisibleCount = () => {
      setVisibleCount(
        window.innerWidth <= mobileBreakpoint ? mobileVisibleCount : desktopVisibleCount
      );
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, [desktopVisibleCount, mobileBreakpoint, mobileVisibleCount]);

  const itemCount = items.length;
  const visibleLimit = Math.min(visibleCount, itemCount);
  const canSlide = itemCount > visibleLimit;

  useEffect(() => {
    setCurrentIndex(0);
    setIsTransitioning(false);
  }, [itemCount, visibleCount]);

  useEffect(() => {
    if (!isTransitioning) return;
    const timer = window.setTimeout(() => setIsTransitioning(false), transitionMs);
    return () => window.clearTimeout(timer);
  }, [isTransitioning, transitionMs]);

  const slide = useCallback((nextDirection: CarouselDirection) => {
    if (!canSlide || isTransitioning) return;

    setDirection(nextDirection);
    setIsTransitioning(true);
    setCurrentIndex((index) => wrapIndex(index + (nextDirection === 'next' ? 1 : -1), itemCount));
  }, [canSlide, isTransitioning, itemCount]);

  const next = useCallback(() => slide('next'), [slide]);
  const prev = useCallback(() => slide('prev'), [slide]);
  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);

  useEffect(() => {
    if (!autoPlayMs || !canSlide || isPaused || isTransitioning) return;

    const interval = window.setInterval(next, autoPlayMs);
    return () => window.clearInterval(interval);
  }, [autoPlayMs, canSlide, isPaused, isTransitioning, next]);

  const visibleItems = useMemo<VisibleCarouselItem<T>[]>(() => {
    if (itemCount === 0) return [];

    if (!canSlide) {
      return items.map((item, index) => ({
        item,
        originalIndex: index,
        position: index,
      }));
    }

    return Array.from({ length: visibleLimit }, (_, position) => {
      const originalIndex = wrapIndex(currentIndex + position, itemCount);
      return {
        item: items[originalIndex],
        originalIndex,
        position,
      };
    });
  }, [canSlide, currentIndex, itemCount, items, visibleLimit]);

  return {
    canSlide,
    currentIndex,
    direction,
    isTransitioning,
    next,
    pause,
    prev,
    resume,
    visibleCount: visibleLimit,
    visibleItems,
  };
};
